import { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import {
  MdDarkMode,
  MdLightMode,
  MdLogout,
  MdMenu,
  MdMenuOpen,
} from 'react-icons/md';
import { Logo } from '@components/ui/Logo';
import { useAuth, useLogout } from '@features/auth/hooks';
import { useColorMode } from '@hooks/useColorMode';
import { SidebarNav } from './SidebarNav';
import { SidebarTopFade } from './SidebarTopFade';
import { SignOutConfirmDialog } from './SignOutConfirmDialog';

const STORAGE_KEY = 'app-shell:sidebar-collapsed';

export function AppShellSidebar() {
  const { user, roles } = useAuth();
  const logout = useLogout();
  const { mode, toggle } = useColorMode();
  const isDark = mode === 'dark';
  const themeLabel = isDark ? 'Switch to light mode' : 'Switch to dark mode';
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(STORAGE_KEY) === 'true',
  );
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const handleConfirmSignOut = () => {
    setConfirmSignOut(false);
    logout.mutate();
  };

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  const initial = (user?.email?.[0] ?? 'U').toUpperCase();

  return (
    <aside
      className={[
        'relative hidden shrink-0 border-r border-border bg-surface md:block',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-[72px]' : 'w-[240px]',
      ].join(' ')}
    >
      <SidebarTopFade />

      <div className="relative flex h-full flex-col">
        {collapsed ? (
          <div className="flex flex-col items-center gap-3 px-3 py-4">
            <IconButton
              size="small"
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
            >
              <MdMenu />
            </IconButton>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-soft ring-1 ring-border">
              <Logo variant="mark" height={22} />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-3 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-soft ring-1 ring-border">
              <Logo variant="mark" height={22} />
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-[14px] font-semibold tracking-tight text-foreground">
                Trendz
              </div>
              <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-foreground-muted">
                Master Data
              </div>
            </div>
            <IconButton
              size="small"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse sidebar"
            >
              <MdMenuOpen />
            </IconButton>
          </div>
        )}

        <SidebarNav roles={roles} collapsed={collapsed} />

        <div className="border-t border-border px-3 py-3">
          {!collapsed && (
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[12px] font-semibold uppercase text-primary">
                {initial}
              </div>
              <div className="min-w-0 flex-1 text-[12px] leading-tight">
                <div className="truncate text-foreground">
                  {user?.email ?? 'Signed in'}
                </div>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={toggle}
            aria-label={themeLabel}
            aria-pressed={isDark}
            title={collapsed ? themeLabel : undefined}
            className={[
              'flex h-10 w-full items-center rounded-md text-[13px] font-medium transition-colors',
              'text-foreground-muted hover:bg-primary/[0.08] hover:text-foreground',
              collapsed ? 'justify-center px-0' : 'gap-2 px-3',
            ].join(' ')}
          >
            {isDark ? <MdLightMode size={18} /> : <MdDarkMode size={18} />}
            {!collapsed && (
              <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setConfirmSignOut(true)}
            disabled={logout.isPending}
            title={collapsed ? 'Sign out' : undefined}
            className={[
              'mt-1 flex h-10 w-full items-center rounded-md text-[13px] font-medium transition-colors',
              'text-foreground-muted hover:bg-danger/10 hover:text-danger',
              'disabled:cursor-not-allowed disabled:opacity-60',
              collapsed ? 'justify-center px-0' : 'gap-2 px-3',
            ].join(' ')}
          >
            <MdLogout size={18} />
            {!collapsed && (
              <span>{logout.isPending ? 'Signing out…' : 'Sign out'}</span>
            )}
          </button>
        </div>
      </div>

      <SignOutConfirmDialog
        open={confirmSignOut}
        onClose={() => setConfirmSignOut(false)}
        onConfirm={handleConfirmSignOut}
        isPending={logout.isPending}
      />
    </aside>
  );
}
